import { create } from 'zustand';
import axios from '../axios-base';
import { appendReferralParams } from '../shared/utility';

export interface CardFact {
    headline?: string;
    copy?: string;
    linkButtonText?: string;
    continueButtonText?: string;
    linkUrl?: string;
    imageUrl?: string;
}

export interface Card {
    id: string; // Unique instance ID on the board
    pairId: string;
    title: string;
    iconName?: string;
    imageUrl?: string;
    isSpecial: boolean;
    fact?: CardFact;
    isFlipped: boolean;
    isMatched: boolean;
}

export interface GameConfig {
    page: string;
    headline: string;
    introText: string;
    showFactModals: boolean;
    enableGoldenPair?: boolean;
    cards: {
        count: number;
        backIconName?: string;
        backIconSize?: number;
        iconSize?: number;
        colorType: 'primary' | 'secondary' | 'custom';
        customColor?: string;
    };
    placeholder: {
        iconName?: string;
        iconSize?: number;
        iconColor?: string;
    };
    ticket: {
        iconName?: string;
    };
    winModal: {
        show: boolean;
        headline: string;
        copy: string;
        button: {
            text: string;
            link: string;
            colorType?: 'yellow' | 'primary' | 'secondary' | 'custom' | '';
            customColor?: string;
            textColorType?: 'dark' | 'white' | 'custom' | '';
            textCustomColor?: string;
        };
    };
}

export interface MemoryGameState {
    cards: Card[];
    cardsPool: Card[];
    flippedCards: Card[];
    matchedPairsCount: number;
    totalPairsCount: number;
    attempts: number;
    activeFact: { pairId?: string; fact?: CardFact } | null;
    goldenFact: CardFact | null;
    isGameCompleted: boolean;
    isGoldenModalOpen: boolean;
    hasTicket: boolean;
    config: GameConfig | null;
    loading: boolean;
    announcement: string;

    fetchGameData: () => Promise<void>;
    initializeBoard: (cardsPool?: Card[], cardCount?: number) => void;
    flipCard: (cardId: string) => void;
    closeFactModal: () => void;
    closeGoldenModal: () => void;
    redeemTicket: (customUrl?: string) => void;
    resetGame: () => void;
}

const DEFAULT_PAIR_CARDS: Card[] = [
    {
        id: '',
        pairId: 'leaf',
        title: 'Blatt',
        iconName: 'IconLeaf',
        isSpecial: false,
        isFlipped: false,
        isMatched: false,
        fact: {
            headline: 'Bäume können Städte um bis zu 8 °C abkühlen.',
            copy: 'Durch Verdunstung und Beschattung senken Stadtbäume die Umgebungstemperatur erheblich – besonders wichtig in Zeiten des Klimawandels.',
        },
    },
    {
        id: '',
        pairId: 'tree',
        title: 'Baum',
        iconName: 'IconTree',
        isSpecial: false,
        isFlipped: false,
        isMatched: false,
        fact: {
            headline: 'Der häufigste Baum Deutschlands ist die Fichte.',
            copy: 'Sie macht rund ein Viertel der deutschen Waldfläche aus, obwohl Mischwälder heute als widerstandsfähiger gelten.',
        },
    },
    {
        id: '',
        pairId: 'sun',
        title: 'Sonne',
        iconName: 'IconSun',
        isSpecial: false,
        isFlipped: false,
        isMatched: false,
        fact: {
            headline: 'Ein Baum filtert bis zu 10 kg CO₂ pro Jahr.',
            copy: 'Durch Photosynthese nehmen Bäume CO₂ auf und speichern den Kohlenstoff in Holz und Wurzeln – ein natürlicher Klimaschutzmechanismus.',
        },
    },
    {
        id: '',
        pairId: 'drop',
        title: 'Wassertropfen',
        iconName: 'IconDroplet',
        isSpecial: false,
        isFlipped: false,
        isMatched: false,
        fact: {
            headline: 'Wälder schützen vor Überschwemmungen.',
            copy: 'Gesunde Waldböden können bis zu 200 Liter Wasser pro Quadratmeter speichern und so Starkregen wirkungsvoll abpuffern.',
        },
    },
    {
        id: '',
        pairId: 'sprout',
        title: 'Keimling',
        iconName: 'IconSeedling',
        isSpecial: false,
        isFlipped: false,
        isMatched: false,
        fact: {
            headline: 'Eichen können über 1.000 Jahre alt werden.',
            copy: 'Sie sind Lebensraum für mehr als 500 Tierarten und gelten als eine der artenreichsten Baumgattungen in ganz Mitteleuropa.',
        },
    },
    {
        id: '',
        pairId: 'gift',
        title: 'Gewinn-Ticket',
        iconName: 'IconGift',
        isSpecial: true,
        isFlipped: false,
        isMatched: false,
        fact: {
            headline: 'Gewinn-Ticket gefunden!',
            copy: 'Chance auf 1 von 100 Wunschgutscheinen sichern!',
        },
    },
    {
        id: '',
        pairId: 'mountain',
        title: 'Bergwald',
        iconName: 'IconMountain',
        isSpecial: false,
        isFlipped: false,
        isMatched: false,
        fact: {
            headline: 'Bergwälder schützen vor Lawinen und Erdrutschen.',
            copy: 'Hochgelegene Wälder wirken als natürliche Schutzschilde – ihre Wurzeln stabilisieren den Boden und bremsen Schneemassen zuverlässig ab.',
        },
    },
];

const shuffleArray = <T>(array: T[]): T[] => {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
};

export const useMemoryGameStore = create<MemoryGameState>((set, get) => ({
    cards: [],
    cardsPool: [],
    flippedCards: [],
    matchedPairsCount: 0,
    totalPairsCount: 0,
    attempts: 0,
    activeFact: null,
    goldenFact: null,
    isGameCompleted: false,
    isGoldenModalOpen: false,
    hasTicket: false,
    config: null,
    loading: true,
    announcement: '',

    fetchGameData: async () => {
        set({ loading: true });
        try {
            let configRes, pairsRes;
            try {
                [configRes, pairsRes] = await Promise.all([
                    axios.get('/memoryGameConfiguration'),
                    axios.get('/memoryGameItemPair'),
                ]);
            } catch {
                [configRes, pairsRes] = await Promise.all([
                    axios.get('/memory-game/config?page=memory-game'),
                    axios.get('/memory-game/cards'),
                ]);
            }

            const rawConfig = Array.isArray(configRes?.data) ? configRes.data[0] : configRes?.data;
            const rawPairs = Array.isArray(pairsRes?.data) ? pairsRes.data : [];

            const config: GameConfig = rawConfig;

            const fetchedCards: Card[] =
                rawPairs.length > 0
                    ? rawPairs.map((item: any) => ({
                          id: '',
                          pairId: item._id || item.pairId,
                          title: item.modal?.headline || item.title || 'Karte',
                          iconName: item.icon?.name,
                          imageUrl: item.modal?.imageUrl,
                          isSpecial: item.ticket?.isGolden || item.isSpecial || false,
                          fact: {
                              headline: item.modal?.headline,
                              copy: item.modal?.copy,
                              linkButtonText: item.modal?.button?.linkButtonText || undefined,
                              continueButtonText: item.modal?.button?.continueButtonText || undefined,
                              linkUrl: item.modal?.button?.link,
                              imageUrl: item.modal?.imageUrl,
                          },
                          isFlipped: false,
                          isMatched: false,
                      }))
                    : DEFAULT_PAIR_CARDS;

            set({ config, cardsPool: fetchedCards, loading: false });
            get().initializeBoard(fetchedCards, config.cards.count);
        } catch (err) {
            console.error('Fehler beim Laden der Memory-Spieldaten:', err);
            set({ loading: false });
            get().initializeBoard(DEFAULT_PAIR_CARDS, 12);
        }
    },

    initializeBoard: (customPool, customCount) => {
        const { cardsPool, config } = get();
        const pool =
            customPool && customPool.length > 0 ? customPool : cardsPool.length > 0 ? cardsPool : DEFAULT_PAIR_CARDS;
        const targetCount = customCount || config?.cards?.count || 12;

        const pairCount = Math.floor(targetCount / 2);
        const enableGolden = config?.enableGoldenPair ?? true;

        const specialPair = enableGolden
            ? pool.find((c) => c.isSpecial || c.pairId === 'gift') || pool[5] || pool[0]
            : pool.find((c) => !c.isSpecial && c.pairId === 'mountain') || pool[6] || pool[0];

        const regularPool = pool.filter((c) => c !== specialPair);
        const randomizedRegularPool = shuffleArray(regularPool);
        const regularPairs = randomizedRegularPool.slice(0, pairCount - 1);
        const selectedPairs = [...regularPairs, specialPair].filter(Boolean);

        const boardCards: Card[] = [];
        selectedPairs.forEach((card) => {
            boardCards.push({
                ...card,
                id: `${card.pairId}-a`,
                isFlipped: false,
                isMatched: false,
            });
            boardCards.push({
                ...card,
                id: `${card.pairId}-b`,
                isFlipped: false,
                isMatched: false,
            });
        });

        const shuffled = shuffleArray(boardCards);
        const goldenFact = specialPair?.isSpecial ? specialPair.fact || null : null;

        set({
            cards: shuffled,
            flippedCards: [],
            matchedPairsCount: 0,
            totalPairsCount: selectedPairs.length,
            attempts: 0,
            activeFact: null,
            goldenFact,
            isGameCompleted: false,
            isGoldenModalOpen: false,
            hasTicket: false,
            announcement: `Spiel gestartet. ${shuffled.length} Karten auf dem Feld. Finde ${selectedPairs.length} Paare.`,
        });
    },

    flipCard: (cardId: string) => {
        const { cards, flippedCards, matchedPairsCount, totalPairsCount, attempts, config } = get();

        if (flippedCards.length >= 2) return;

        const targetIndex = cards.findIndex((c) => c.id === cardId);
        if (targetIndex === -1) return;

        const targetCard = cards[targetIndex];
        if (targetCard.isFlipped || targetCard.isMatched) return;

        const updatedCards = [...cards];
        updatedCards[targetIndex] = { ...targetCard, isFlipped: true };

        const newFlipped = [...flippedCards, updatedCards[targetIndex]];

        if (newFlipped.length === 1) {
            set({
                cards: updatedCards,
                flippedCards: newFlipped,
                announcement: `Karte ${targetCard.title} aufgedeckt. Wähle eine zweite Karte.`,
            });
            return;
        }

        if (newFlipped.length === 2) {
            const [first, second] = newFlipped;
            const isMatch = first.pairId === second.pairId;
            const newAttempts = attempts + 1;

            // Immediately set second card as flipped so 3D rotation animation starts instantly on click
            set({
                cards: updatedCards,
                flippedCards: newFlipped,
                attempts: newAttempts,
                announcement: isMatch
                    ? `Paar gefunden: ${first.title}!`
                    : `Kein Paar: ${first.title} und ${second.title}. Karten werden wieder verdeckt.`,
            });

            if (isMatch) {
                const newMatchedCount = matchedPairsCount + 1;
                const isCompleted = newMatchedCount >= totalPairsCount;
                const isGolden = first.isSpecial && (config?.enableGoldenPair ?? true);

                // Wait 550ms so 400ms 3D rotation completes smoothly before setting matched status and showing modal
                setTimeout(() => {
                    const currentCards = get().cards;
                    const matchedCards = currentCards.map((c) =>
                        c.pairId === first.pairId ? { ...c, isMatched: true, isFlipped: false } : c
                    );

                    set({
                        cards: matchedCards,
                        flippedCards: [],
                        matchedPairsCount: newMatchedCount,
                        isGameCompleted: isCompleted,
                        isGoldenModalOpen: isGolden && !isCompleted,
                        hasTicket: get().hasTicket || isGolden,
                        activeFact:
                            !isGolden && !isCompleted && config?.showFactModals
                                ? { pairId: first.pairId, fact: first.fact }
                                : null,
                        announcement: `${newMatchedCount} von ${totalPairsCount} Paaren aufgedeckt.`,
                    });
                }, 550);
            } else {
                setTimeout(() => {
                    const resetFlipped = get().cards.map((c) =>
                        c.id === first.id || c.id === second.id ? { ...c, isFlipped: false } : c
                    );
                    set({
                        cards: resetFlipped,
                        flippedCards: [],
                    });
                }, 1000);
            }
        }
    },

    closeFactModal: () => {
        set({ activeFact: null });
    },

    closeGoldenModal: () => {
        set({ isGoldenModalOpen: false });
    },

    redeemTicket: (customUrl?: string) => {
        const rawUrl = customUrl || get().goldenFact?.linkUrl || get().config?.winModal?.button?.link;
        const targetUrl = appendReferralParams(rawUrl);
        if (targetUrl && targetUrl !== '#') {
            window.open(targetUrl, '_blank', 'noopener');
        } else {
            alert('Gewinn-Ticket wird eingelöst!');
        }
    },

    resetGame: () => {
        get().initializeBoard();
    },
}));

export default useMemoryGameStore;
